import { Spacing } from '@persona-id/design-tokens';
import { FC, useMemo, useState } from 'react';
import styled from 'styled-components';

import Divider from 'dashboard/components/Divider';
import { AnalyticsQueryTemplate } from 'dashboard/models/analytics-query-template';
import Button from 'design-system/components/Button';
import Popover, { PopoverCard, PopoverContent } from 'design-system/components/Popover';
import Select, { Option } from 'design-system/components/Select';

interface LoadQueryTemplatePopoverProps {
  loading: boolean;
  loadAction: (template: AnalyticsQueryTemplate) => void;
  queryTemplates: AnalyticsQueryTemplate[];
}

const StyledDivider = styled(Divider)`
  margin: 0 -${Spacing[5]};
`;

const ContentContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const InputContainer = styled(ContentContainer)`
  width: 400px;
  flex-direction: column;
  align-items: start;
  padding-bottom: ${Spacing[4]};
`;

const ButtonGroup = styled(ContentContainer)`
  display: flex;
  justify-content: flex-end;
  padding-top: ${Spacing[1]};

  & > button:last-child {
    margin-left: ${Spacing[3]};
  }
`;

const LoadQueryTemplatePopover: FC<LoadQueryTemplatePopoverProps> = ({
  loading,
  loadAction,
  // Note: (amateo) temporary value while in development
  queryTemplates,
}: LoadQueryTemplatePopoverProps) => {
  // Note: (amateo) to meet design requirements, we use local state to manage the popover open state rather
  // than letting the Popover component manage it itself. This is because the Popover component does not allow for
  // the popover to be closed by external actions (e.g. clicking a button in the popover content).
  const [isOpen, setIsOpen] = useState(false);
  const onOpenChange = (open: boolean) => {
    setSelectionError(false);
    setIsOpen(open);
  };

  const [selectionError, setSelectionError] = useState(false);
  const [selectedQueryTemplate, setSelectedQueryTemplate] = useState<string | null>();
  const handleLoadAction = () => {
    if (selectedQueryTemplate == null) {
      setSelectionError(true);
      return;
    }
    const queryTemplate = queryTemplates.find(({ id }) => id === selectedQueryTemplate);
    if (queryTemplate != null) {
      loadAction(queryTemplate);
    }
    setIsOpen(false);
  };

  const handleCancel = () => {
    setSelectedQueryTemplate(null);
    setIsOpen(false);
  };

  const handleQueryTemplateChange = (id: string) => setSelectedQueryTemplate(id);

  const queryTemplateOptions = useMemo(
    () =>
      queryTemplates.map(({ id, attributes }) => ({
        value: id,
        label: attributes.name,
      })),
    [queryTemplates],
  );

  return (
    <Popover
      open={isOpen}
      onOpenChange={onOpenChange}
      contentProps={{
        noPadding: true,
        showCloseButton: true,
      }}
      trigger={
        <Button id='metrics-load_template' data-test='metrics-load_template' variant='basic'>
          Load
        </Button>
      }
    >
      <PopoverContent>
        <PopoverCard side='bottom' align='end'>
          Saved Queries
          <InputContainer>
            <Select
              placeholder='Select template'
              onValueChange={handleQueryTemplateChange}
              hasError={selectionError}
              disabled={queryTemplateOptions.length === 0}
            >
              {queryTemplateOptions.map((opt, i) => (
                <Option key={`interval-options-${i}-${opt.label}`} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </InputContainer>
          <StyledDivider full={true} />
          <ButtonGroup>
            <Button
              id='metrics-inquiries-cancel_query_template_create'
              date-test='metrics-cancel_query_template_create'
              width='auto'
              variant='basic'
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              id='metrics-inquiries-create_query_template'
              data-test='metrics-create_query_template'
              width='auto'
              variant='primary'
              onClick={handleLoadAction}
              disabled={loading || selectedQueryTemplate == null || selectionError}
            >
              Load
            </Button>
          </ButtonGroup>
        </PopoverCard>
      </PopoverContent>
    </Popover>
  );
};

export default LoadQueryTemplatePopover;
